import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  selectActiveProfile,
  selectActiveProfileId,
  selectProfilePermissions,
  selectUserPermissions,
} from '../containers/App/selectors';

function usePermissions(targetId) {
  const profilePermissions = useSelector(selectProfilePermissions());
  const userPermissions = useSelector(selectUserPermissions());
  const userProfile = useSelector(selectActiveProfile());

  const getNewPermissions = useCallback(() => {
    const permSet = new Set();
    const isPermReady = profilePermissions.loadedOnce || profilePermissions.error;
    if (isPermReady) {
      profilePermissions.permissions.forEach(p => permSet.add(p.value));
      userPermissions.permissions.forEach(p => permSet.add(p.identifier));
    }
    const hasAgencyAccess =
      userProfile &&
      userProfile.agencies &&
      ['agent', 'admin'].includes(userProfile.agencies.find(a => a.id === targetId)?.status);
    const hasCompanyAccess =
      userProfile &&
      userProfile.companies &&
      userProfile.companies.find(a => a?.company?.id === targetId)?.status === 'employee';
    const hasFestivalAccess =
      userProfile &&
      userProfile.festivals &&
      userProfile.festivals.find(a => a?.festival?.id === targetId)?.status === 'employee';

    const isTargetMyProfile = userProfile?.id === targetId;

    const newPermissions = {
      castingToolPermissions: {
        hasAccess: permSet.has('applications.frontbase.castingTool'),
      },
      companyPermissions: {
        canEdit: permSet.has('companies.update.all') || (permSet.has('companies.update.own') && hasCompanyAccess),
      },
      festivalPermissions: {
        canEdit: permSet.has('festivals.update.all') || (permSet.has('festivals.update.own') && hasFestivalAccess), // change on festival permissions when api is ready
      },
      productionPermissions: {
        canDeleteAsAdmin: permSet.has('productions.delete.all') && permSet.has('apiConstants.override'),
        canDelete: permSet.has('productions.delete.all'),
        canCancel: permSet.has('productions.update.own'),
        canPublish: permSet.has('productionActions.publish.own'),
        canPublishAll: permSet.has('productionActions.publish.all'),
      },
      artistPermissions: {
        canEdit: permSet.has('profiles.update.all') || (permSet.has('profiles.update.own') && isTargetMyProfile),
        repertoire: {
          canView:
            permSet.has('profileRepertoire.get.all') || (permSet.has('profileRepertoire.get.own') && isTargetMyProfile),
          canEdit:
            permSet.has('profileRepertoire.update.all') ||
            (permSet.has('profileRepertoire.update.own') && isTargetMyProfile),
          canDelete:
            permSet.has('profileRepertoire.delete.all') ||
            (permSet.has('profileRepertoire.delete.own') && isTargetMyProfile),
          canAdd:
            permSet.has('profileRepertoire.create.all') ||
            (permSet.has('profileRepertoire.create.own') && isTargetMyProfile),
        },
        redMaskRequest: permSet.has('applications.frontbase.redMask'),
      },
      agencyPermissions: {
        canEdit: permSet.has('agencies.update.all') || (permSet.has('agencies.update.own') && hasAgencyAccess),
        canAddOffice:
          permSet.has('agencyOffices.create.all') || (permSet.has('agencyOffices.create.own') && hasAgencyAccess),
        canDeleteOffice:
          permSet.has('agencyOffices.delete.all') || (permSet.has('agencyOffices.delete.own') && hasAgencyAccess),
        canAddArtist:
          permSet.has('agencyArtists.create.all') || (permSet.has('agencyArtists.create.own') && hasAgencyAccess),
      },
      isAdmin: permSet.has('applications.frontbase.admin'),
      isTranslator: permSet.has('applications.frontbase.translator'),
      isPaidAccess: permSet.has('applications.frontbase.paidAccess'),
    };
    return newPermissions;
  }, [
    profilePermissions.error,
    profilePermissions.loadedOnce,
    profilePermissions.permissions,
    targetId,
    userPermissions.permissions,
    userProfile,
  ]);

  const [permissions, setPermissions] = useState(getNewPermissions());

  useEffect(() => {
    setPermissions(getNewPermissions());
  }, [getNewPermissions, profilePermissions, targetId]);
  return permissions;
}

export function useProductionPermissions() {
  const activeProfileId = useSelector(selectActiveProfileId());
  const { productionPermissions, isAdmin } = usePermissions(activeProfileId);

  const canPublishAll = isAdmin || productionPermissions.canPublishAll;

  return { canPublishAll };
}

export default usePermissions;
